/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

const MINUTE_MS = 60_000;

/**
 * The wait used when the server did not say, or said something unusable. Only
 * camping is currently believed to send a `retry-after`, so this is the common
 * path rather than an edge case.
 */
const DEFAULT_WAIT_MS = MINUTE_MS;

/**
 * Longest wait the notice will count down. The WAF's widest window is five minutes,
 * so a longer wait describes a clock skew or a misconfiguration rather than a window.
 * Above it the header says nothing usable, so it reads the same as an absent header
 * and falls back to the default rather than counting down an hour.
 */
const COUNTDOWN_CEILING_MS = 5 * MINUTE_MS;

/**
 * Shortest wait the notice will honor. The WAF's windows run one to five minutes, so
 * a wait of a few seconds is a window almost elapsed, or a header from some other
 * layer, and neither is worth what the notice does with it: a dialog that appears and
 * reloads the page inside four seconds explains nothing to the visitor it
 * interrupted, and the suppression window derived from the same wait would be back to
 * the POC's four-second grace period, which the retrying clients and per-month
 * fan-outs on this platform outlast trivially.
 *
 * Below it the wait falls back to the default rather than being read as usable, so a
 * short wait still gives the visitor a full minute to read the notice — long enough
 * to read it, and already a milestone the announcements know about.
 */
const MIN_WAIT_MS = MINUTE_MS / 2;

/**
 * How long the module believes the wait to be, always a value the notice can count
 * down.
 *
 * Anything outside the watchable window `[MIN_WAIT_MS, COUNTDOWN_CEILING_MS]` — absent,
 * negative, too short, or too long — falls back to the one-minute default so the
 * notice always has a real countdown to show. Negative values cannot come from
 * `parseRetryAfterMs`, which reads them as absent, but can come from a service
 * computing its own wait for `reportRateLimit`.
 *
 * Shared by the countdown and the suppression window, so both agree on the wait
 * rather than each normalizing it its own way. The bounds belong here for the same
 * reason: the countdown and the window have to end together, and a bound applied at
 * the notice would leave the window unprotected.
 */
export function resolveWaitMs(retryAfterMs: number | null): number {
    if (
        retryAfterMs === null ||
        retryAfterMs < MIN_WAIT_MS ||
        retryAfterMs > COUNTDOWN_CEILING_MS
    ) {
        return DEFAULT_WAIT_MS;
    }

    return retryAfterMs;
}

/**
 * Formats remaining milliseconds as `m:ss`, at every value, so the format does not
 * shift as the countdown shrinks.
 *
 * Rounds up, so a one-minute wait reads `1:00` for its first second rather than
 * flashing `0:59` before anyone can read it.
 */
export function formatCountdown(remainingMs: number): string {
    const totalSeconds = Math.ceil(Math.max(remainingMs, 0) / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
